
import { useState } from 'react';

export interface UserInfo {
  id: number;
  name: string;
  email: string;
  role: string;
  status: string;
  color: string;
  phone: string;
  department: string;
  joinDate: string;
  lastLogin: string;
  remark: string;
  station: string;
  avatar: string;
  gender: string;
  idCard: string;
  address: string;
  emergencyContact: string;
  emergencyPhone: string;
  permissions: string[];
}

const ROLE_PERMISSIONS: Record<
  string,
  { label: string; permissions: string[]; desc: string }
> = {
  系统管理员: {
    label: '系统管理员',
    desc: '拥有全部功能权限，可管理用户、配置系统',
    permissions: [
      '台账查询',
      '快速录入',
      '报表中心',
      '数据报送',
      '客户管理',
      '系统设置',
      '用户管理',
      '数据导出',
      '数据删除',
    ],
  },
  录入员: {
    label: '录入员',
    desc: '可进行数据录入和查询，无系统管理权限',
    permissions: [
      '台账查询',
      '快速录入',
      '报表中心',
      '客户管理',
      '数据导出',
    ],
  },
  查看员: {
    label: '查看员',
    desc: '仅可查看台账和报表，不可修改数据',
    permissions: ['台账查询', '报表中心'],
  },
  财务专员: {
    label: '财务专员',
    desc: '可查看财务相关报表和台账，可导出数据',
    permissions: ['台账查询', '报表中心', '数据导出'],
  },
  站点负责人: {
    label: '站点负责人',
    desc: '管理所属站点的收购录入和查询',
    permissions: [
      '台账查询',
      '快速录入',
      '报表中心',
      '客户管理',
      '数据导出',
    ],
  },
};

const ALL_PERMISSIONS = [
  '台账查询',
  '快速录入',
  '报表中心',
  '数据报送',
  '客户管理',
  '系统设置',
  '用户管理',
  '数据导出',
  '数据删除',
];

const initialUsers: UserInfo[] = [
  {
    id: 1,
    name: '张建国',
    email: 'admin@grain.com',
    role: '系统管理员',
    status: '正常',
    color: 'rose',
    phone: '138-0000-0001',
    department: '管理层',
    joinDate: '2024-01-01',
    lastLogin: '2026-02-18 09:32',
    remark: '超级管理员账号，拥有全部权限',
    station: '全部站点',
    avatar:
      'https://public.readdy.ai/ai/img_res/f53c7e20c65a5e39d0a91c854122979e.jpg',
    gender: '男',
    idCard: '410101198801010001',
    address: '河南省郑州市金水区',
    emergencyContact: '张某某',
    emergencyPhone: '139-0000-0001',
    permissions: ALL_PERMISSIONS,
  },
  {
    id: 2,
    name: '李秀梅',
    email: 'lixiumei@grain.com',
    role: '录入员',
    status: '正常',
    color: 'teal',
    phone: '138-0000-0002',
    department: '仓储部',
    joinDate: '2024-06-15',
    lastLogin: '2026-02-17 14:20',
    remark: '负责日常收粮和销售数据录入',
    station: '中原路收购站',
    avatar:
      'https://readdy.ai/api/search-image?query=professional%20Chinese%20female%20staff%20portrait%20headshot%20clean%20white%20background%20office%20attire%20friendly%20smile&width=80&height=80&seq=user2&orientation=squarish',
    gender: '女',
    idCard: '410101199501010002',
    address: '河南省郑州市惠济区',
    emergencyContact: '李某某',
    emergencyPhone: '139-0000-0002',
    permissions: ROLE_PERMISSIONS['录入员'].permissions,
  },
  {
    id: 3,
    name: '王志远',
    email: 'wangzhiyuan@grain.com',
    role: '查看员',
    status: '正常',
    color: 'amber',
    phone: '138-0000-0003',
    department: '财务部',
    joinDate: '2025-03-10',
    lastLogin: '2026-02-16 16:45',
    remark: '仅查看台账和报表数据',
    station: '农业路收购站',
    avatar:
      'https://readdy.ai/api/search-image?query=professional%20Chinese%20male%20accountant%20portrait%20headshot%20clean%20white%20background%20business%20casual%20attire&width=80&height=80&seq=user3&orientation=squarish',
    gender: '男',
    idCard: '410101199201010003',
    address: '河南省郑州市二七区',
    emergencyContact: '王某某',
    emergencyPhone: '139-0000-0003',
    permissions: ROLE_PERMISSIONS['查看员'].permissions,
  },
  {
    id: 4,
    name: '陈晓燕',
    email: 'chenxiaoyan@grain.com',
    role: '财务专员',
    status: '正常',
    color: 'indigo',
    phone: '138-0000-0004',
    department: '财务部',
    joinDate: '2024-09-01',
    lastLogin: '2026-02-15 10:30',
    remark: '负责财务报表审核和数据导出',
    station: '全部站点',
    avatar:
      'https://readdy.ai/api/search-image?query=professional%20Chinese%20female%20finance%20staff%20portrait%20headshot%20clean%20white%20background%20formal%20attire%20glasses&width=80&height=80&seq=user4&orientation=squarish',
    gender: '女',
    idCard: '410101199801010004',
    address: '河南省郑州市管城区',
    emergencyContact: '陈某某',
    emergencyPhone: '139-0000-0004',
    permissions: ROLE_PERMISSIONS['财务专员'].permissions,
  },
  {
    id: 5,
    name: '刘大伟',
    email: 'liudawei@grain.com',
    role: '站点负责人',
    status: '停用',
    color: 'gray',
    phone: '138-0000-0005',
    department: '运营部',
    joinDate: '2023-11-20',
    lastLogin: '2026-01-10 08:15',
    remark: '南部收购站负责人',
    station: '南部收购站',
    avatar:
      'https://readdy.ai/api/search-image?query=professional%20Chinese%20male%20supervisor%20portrait%20headshot%20clean%20white%20background%20work%20uniform%20serious%20expression&width=80&height=80&seq=user5&orientation=squarish',
    gender: '男',
    idCard: '410101198501010005',
    address: '河南省郑州市新郑市',
    emergencyContact: '刘某某',
    emergencyPhone: '139-0000-0005',
    permissions: ROLE_PERMISSIONS['站点负责人'].permissions,
  },
];

const stationOptions = [
  '全部站点',
  '中原路收购站',
  '农业路收购站',
  '南部收购站',
];

export default function UserTab({
  showSuccess,
}: {
  showSuccess: (msg: string) => void;
}) {
  const [users, setUsers] = useState<UserInfo[]>(initialUsers);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingUser, setEditingUser] = useState<UserInfo | null>(null);
  const [editTab, setEditTab] = useState<'basic' | 'permission' | 'other'>('basic');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteId, setDeleteId] = useState<number | null>(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [filterRole, setFilterRole] = useState('全部');

  const filtered = users.filter((u) => {
    const matchSearch =
      u.name.includes(searchText) ||
      u.email.includes(searchText) ||
      u.phone.includes(searchText);
    const matchRole = filterRole === '全部' || u.role === filterRole;
    return matchSearch && matchRole;
  });

  const handleEdit = (user: UserInfo) => {
    setEditingUser({ ...user, permissions: [...user.permissions] });
    setEditTab('basic');
    setShowEditModal(true);
  };

  const handleSave = () => {
    if (!editingUser) return;
    setUsers((prev) =>
      prev.map((u) => (u.id === editingUser.id ? editingUser : u)),
    );
    setShowEditModal(false);
    setEditingUser(null);
    showSuccess('用户信息保存成功');
  };

  const handleDelete = () => {
    if (deleteId !== null) {
      setUsers((prev) => prev.filter((u) => u.id !== deleteId));
    }
    setShowDeleteConfirm(false);
    setDeleteId(null);
    showSuccess('用户已删除');
  };

  const handleRoleChange = (role: string) => {
    if (!editingUser) return;
    const rolePerms = ROLE_PERMISSIONS[role]?.permissions || [];
    setEditingUser({ ...editingUser, role, permissions: [...rolePerms] });
  };

  const togglePermission = (perm: string) => {
    if (!editingUser) return;
    const perms = editingUser.permissions.includes(perm)
      ? editingUser.permissions.filter((p) => p !== perm)
      : [...editingUser.permissions, perm];
    setEditingUser({ ...editingUser, permissions: perms });
  };

  const handleAddUser = () => {
    const timestamp = Date.now();
    const newUser: UserInfo = {
      id: timestamp,
      name: '新用户',
      email: `user${timestamp}@grain.com`,
      role: '查看员',
      status: '正常',
      color: 'teal',
      phone: '',
      department: '',
      joinDate: new Date().toISOString().split('T')[0],
      lastLogin: '-',
      remark: '',
      station: '全部站点',
      avatar: '',
      gender: '男',
      idCard: '',
      address: '',
      emergencyContact: '',
      emergencyPhone: '',
      permissions: ROLE_PERMISSIONS['查看员'].permissions,
    };
    setEditingUser(newUser);
    setEditTab('basic');
    setShowAddModal(true);
    setShowEditModal(true);
  };

  const handleSaveNew = () => {
    if (!editingUser) return;
    setUsers((prev) => [...prev, editingUser]);
    setShowEditModal(false);
    setShowAddModal(false);
    setEditingUser(null);
    showSuccess('用户添加成功');
  };

  const roleColors: Record<string, string> = {
    系统管理员: 'bg-rose-100 text-rose-700',
    录入员: 'bg-teal-100 text-teal-700',
    查看员: 'bg-amber-100 text-amber-700',
    财务专员: 'bg-indigo-100 text-indigo-600',
    站点负责人: 'bg-green-100 text-green-700',
  };

  return (
    <div className="space-y-5">
      {/* 统计卡片 */}
      <div className="grid grid-cols-4 gap-4">
        {[
          {
            label: '用户总数',
            value: users.length,
            icon: 'ri-team-line',
            color: 'text-teal-600',
            bg: 'bg-teal-50',
          },
          {
            label: '正常账号',
            value: users.filter((u) => u.status === '正常').length,
            icon: 'ri-checkbox-circle-line',
            color: 'text-green-600',
            bg: 'bg-green-50',
          },
          {
            label: '停用账号',
            value: users.filter((u) => u.status === '停用').length,
            icon: 'ri-forbid-line',
            color: 'text-rose-500',
            bg: 'bg-rose-50',
          },
          {
            label: '角色类型',
            value: Object.keys(ROLE_PERMISSIONS).length,
            icon: 'ri-shield-user-line',
            color: 'text-amber-600',
            bg: 'bg-amber-50',
          },
        ].map((item, i) => (
          <div
            key={i}
            className="bg-white rounded-xl border border-gray-100 p-4 flex items-center space-x-3 shadow-sm"
          >
            <div className={`w-10 h-10 ${item.bg} rounded-xl flex items-center justify-center`}>
              <i className={`${item.icon} ${item.color} text-xl`}></i>
            </div>
            <div>
              <p className="text-xs text-gray-500">{item.label}</p>
              <p className="text-xl font-bold text-gray-800">{item.value}</p>
            </div>
          </div>
        ))}
      </div>

      {/* 用户列表 */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100">
        <div className="p-5 border-b border-gray-200 flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="relative">
              <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm"></i>
              <input
                type="text"
                placeholder="搜索姓名/邮箱/手机号"
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                className="pl-9 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-teal-500 focus:border-transparent w-56"
              />
            </div>
            <select
              value={filterRole}
              onChange={(e) => setFilterRole(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-teal-500 focus:border-transparent cursor-pointer"
            >
              <option>全部</option>
              {Object.keys(ROLE_PERMISSIONS).map((r) => (
                <option key={r}>{r}</option>
              ))}
            </select>
          </div>
          <button
            onClick={handleAddUser}
            className="px-5 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-all text-sm font-medium whitespace-nowrap cursor-pointer"
          >
            <i className="ri-add-line mr-2"></i>添加用户
          </button>
        </div>

        <div className="divide-y divide-gray-100">
          {filtered.map((user) => (
            <div
              key={user.id}
              className="flex items-center justify-between px-5 py-4 hover:bg-gray-50 transition-colors"
            >
              <div className="flex items-center space-x-4">
                <div className="w-12 h-12 rounded-full overflow-hidden flex-shrink-0 border-2 border-gray-100">
                  {user.avatar ? (
                    <img
                      src={user.avatar}
                      alt={user.name}
                      className="w-full h-full object-cover object-top"
                    />
                  ) : (
                    <div
                      className={`w-full h-full bg-${user.color}-100 flex items-center justify-center`}
                    >
                      <i className={`ri-user-line text-${user.color}-600 text-xl`}></i>
                    </div>
                  )}
                </div>
                <div>
                  <div className="flex items-center space-x-2">
                    <p className="text-sm font-semibold text-gray-800">{user.name}</p>
                    <span
                      className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                        roleColors[user.role] ||
                        'bg-gray-100 text-gray-600'
                      }`}
                    >
                      {user.role}
                    </span>
                    <span
                      className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                        user.status === '正常'
                          ? 'bg-green-100 text-green-700'
                          : 'bg-gray-100 text-gray-500'
                      }`}
                    >
                      {user.status}
                    </span>
                  </div>
                  <div className="flex items-center space-x-3 mt-1">
                    <span className="text-xs text-gray-500 flex items-center space-x-1">
                      <i className="ri-phone-line text-gray-400"></i>
                      <span>{user.phone || '-'}</span>
                    </span>
                    <span className="text-xs text-gray-400">{user.email}</span>
                  </div>
                </div>
              </div>
              <div className="flex items-center space-x-5">
                <div className="text-center">
                  <p className="text-xs text-gray-400">部门</p>
                  <p className="text-xs font-medium text-gray-700">{user.department || '-'}</p>
                </div>
                <div className="text-center">
                  <p className="text-xs text-gray-400">所属站点</p>
                  <p className="text-xs font-medium text-gray-700">{user.station}</p>
                </div>
                <div className="text-center">
                  <p className="text-xs text-gray-400">最近登录</p>
                  <p className="text-xs font-medium text-gray-700">{user.lastLogin}</p>
                </div>
                <div className="flex items-center space-x-1.5">
                  <button
                    onClick={() => handleEdit(user)}
                    className="w-8 h-8 flex items-center justify-center text-teal-600 hover:bg-teal-50 rounded-lg transition-colors cursor-pointer"
                    title="编辑"
                  >
                    <i className="ri-edit-line text-lg"></i>
                  </button>
                  <button
                    onClick={() => {
                      setDeleteId(user.id);
                      setShowDeleteConfirm(true);
                    }}
                    className="w-8 h-8 flex items-center justify-center text-rose-500 hover:bg-rose-50 rounded-lg transition-colors cursor-pointer"
                    title="删除"
                  >
                    <i className="ri-delete-bin-line text-lg"></i>
                  </button>
                </div>
              </div>
            </div>
          ))}
          {filtered.length === 0 && (
            <div className="text-center py-12 text-gray-400">
              <i className="ri-user-search-line text-4xl mb-2"></i>
              <p className="text-sm">未找到匹配用户</p>
            </div>
          )}
        </div>
      </div>

      {/* 角色权限说明 */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
        <h3 className="text-sm font-semibold text-gray-800 mb-4 flex items-center">
          <i className="ri-shield-user-line mr-2 text-teal-600"></i>系统角色权限说明
        </h3>
        <div className="grid grid-cols-5 gap-3">
          {Object.entries(ROLE_PERMISSIONS).map(([role, info]) => (
            <div
              key={role}
              className="border border-gray-200 rounded-xl p-3 hover:border-teal-200 transition-colors"
            >
              <div
                className={`inline-block px-2.5 py-1 rounded-full text-xs font-medium mb-2 ${
                  roleColors[role] || 'bg-gray-100 text-gray-600'
                }`}
              >
                {role}
              </div>
              <p className="text-xs text-gray-500 mb-2">{info.desc}</p>
              <div className="space-y-1">
                {info.permissions.map((p) => (
                  <div
                    key={p}
                    className="flex items-center space-x-1.5 text-xs text-gray-600"
                  >
                    <i className="ri-check-line text-teal-500 text-xs"></i>
                    <span>{p}</span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* 编辑/新增弹窗 */}
      {showEditModal && editingUser && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[92vh] flex flex-col">
            {/* 头部 */}
            <div className="border-b border-gray-200 px-6 py-5 flex items-center justify-between flex-shrink-0">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-teal-100 rounded-xl flex items-center justify-center">
                  <i className="ri-user-settings-line text-teal-600 text-xl"></i>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-800">
                    {showAddModal ? '新增用户' : '编辑用户信息'}
                  </h3>
                  <p className="text-xs text-gray-500 mt-0.5">
                    管理用户资料、权限和系统角色
                  </p>
                </div>
              </div>
              <button
                onClick={() => {
                  setShowEditModal(false);
                  setShowAddModal(false);
                  setEditingUser(null);
                }}
                className="w-9 h-9 flex items-center justify-center text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors cursor-pointer"
              >
                <i className="ri-close-line text-xl"></i>
              </button>
            </div>

            {/* Tab */}
            <div className="flex border-b border-gray-200 px-6 flex-shrink-0">
              {[
                { key: 'basic', icon: 'ri-user-line', label: '基本信息' },
                { key: 'permission', icon: 'ri-shield-user-line', label: '权限设置' },
                { key: 'other', icon: 'ri-file-list-line', label: '其他信息' },
              ].map((tab) => (
                <button
                  key={tab.key}
                  onClick={() => setEditTab(tab.key as any)}
                  className={`flex items-center space-x-2 px-5 py-3.5 text-sm font-medium border-b-2 transition-all cursor-pointer whitespace-nowrap ${
                    editTab === tab.key
                      ? 'border-teal-600 text-teal-700'
                      : 'border-transparent text-gray-500 hover:text-gray-700'
                  }`}
                >
                  <i className={tab.icon}></i>
                  <span>{tab.label}</span>
                </button>
              ))}
            </div>

            {/* 内容 */}
            <div className="flex-1 overflow-y-auto p-6">
              {/* 基本信息 */}
              {editTab === 'basic' && (
                <div className="space-y-5">
                  {/* 头像区域 */}
                  <div className="flex items-center space-x-5 p-4 bg-gray-50 rounded-xl">
                    <div className="w-20 h-20 rounded-full overflow-hidden border-4 border-white shadow-md flex-shrink-0">
                      {editingUser.avatar ? (
                        <img
                          src={editingUser.avatar}
                          alt={editingUser.name}
                          className="w-full h-full object-cover object-top"
                        />
                      ) : (
                        <div className="w-full h-full bg-teal-100 flex items-center justify-center">
                          <i className="ri-user-line text-teal-600 text-3xl"></i>
                        </div>
                      )}
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-gray-800">
                        {editingUser.name}
                      </p>
                      <p className="text-xs text-gray-500 mt-0.5">{editingUser.email}</p>
                      <button className="mt-2 px-3 py-1.5 bg-teal-50 text-teal-700 rounded-lg text-xs font-medium hover:bg-teal-100 transition-colors cursor-pointer whitespace-nowrap">
                        <i className="ri-camera-line mr-1"></i>更换照片
                      </button>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    {[
                      { label: '姓名', key: 'name', required: true },
                      {
                        label: '性别',
                        key: 'gender',
                        type: 'select',
                        options: ['男', '女'],
                      },
                      { label: '手机号码', key: 'phone', required: true },
                      { label: '邮箱地址', key: 'email', required: true },
                      { label: '所属部门', key: 'department' },
                      {
                        label: '所属站点',
                        key: 'station',
                        type: 'select',
                        options: stationOptions,
                      },
                      {
                        label: '账号状态',
                        key: 'status',
                        type: 'select',
                        options: ['正常', '停用'],
                      },
                      { label: '入职日期', key: 'joinDate', type: 'date' },
                    ].map((field) => (
                      <div key={field.key}>
                        <label className="block text-sm font-medium text-gray-700 mb-1.5">
                          {field.label}
                          {field.required && (
                            <span className="text-rose-500 ml-1">*</span>
                          )}
                        </label>
                        {field.type === 'select' ? (
                          <select
                            value={
                              editingUser[
                                field.key as keyof UserInfo
                              ] as string
                            }
                            onChange={(e) =>
                              setEditingUser({
                                ...editingUser,
                                [field.key]: e.target.value,
                              })
                            }
                            className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-teal-500 focus:border-transparent cursor-pointer"
                          >
                            {field.options?.map((o) => (
                              <option key={o}>{o}</option>
                            ))}
                          </select>
                        ) : (
                          <input
                            type={field.type || 'text'}
                            value={
                              editingUser[
                                field.key as keyof UserInfo
                              ] as string
                            }
                            onChange={(e) =>
                              setEditingUser({
                                ...editingUser,
                                [field.key]: e.target.value,
                              })
                            }
                            className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                          />
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* 权限设置 */}
              {editTab === 'permission' && (
                <div className="space-y-5">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      系统角色
                    </label>
                    <div className="grid grid-cols-3 gap-3">
                      {Object.entries(ROLE_PERMISSIONS).map(([role, info]) => (
                        <button
                          key={role}
                          onClick={() => handleRoleChange(role)}
                          className={`p-3 rounded-xl border-2 text-left transition-all cursor-pointer ${
                            editingUser.role === role
                              ? 'border-teal-500 bg-teal-50'
                              : 'border-gray-200 hover:border-teal-200'
                          }`}
                        >
                          <div className="flex items-center justify-between mb-1">
                            <span
                              className={`text-xs font-semibold px-2 py-0.5 rounded-full ${
                                roleColors[role] ||
                                'bg-gray-100 text-gray-600'
                              }`}
                            >
                              {role}
                            </span>
                            {editingUser.role === role && (
                              <i className="ri-check-circle-fill text-teal-500"></i>
                            )}
                          </div>
                          <p className="text-xs text-gray-500 mt-1">{info.desc}</p>
                        </button>
                      ))}
                    </div>
                  </div>

                  <div>
                    <div className="flex items-center justify-between mb-3">
                      <label className="text-sm font-medium text-gray-700">
                        功能权限（可自定义调整）
                      </label>
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() =>
                            setEditingUser({
                              ...editingUser,
                              permissions: [...ALL_PERMISSIONS],
                            })
                          }
                          className="text-xs text-teal-600 hover:underline cursor-pointer whitespace-nowrap"
                        >
                          全选
                        </button>
                        <span className="text-gray-300">|</span>
                        <button
                          onClick={() =>
                            setEditingUser({
                              ...editingUser,
                              permissions: [],
                            })
                          }
                          className="text-xs text-gray-500 hover:underline cursor-pointer whitespace-nowrap"
                        >
                          清空
                        </button>
                      </div>
                    </div>
                    <div className="grid grid-cols-3 gap-2">
                      {ALL_PERMISSIONS.map((perm) => {
                        const checked = editingUser.permissions.includes(perm);
                        return (
                          <button
                            key={perm}
                            onClick={() => togglePermission(perm)}
                            className={`flex items-center space-x-2 px-3 py-2.5 rounded-lg border transition-all cursor-pointer text-sm ${
                              checked
                                ? 'border-teal-400 bg-teal-50 text-teal-700'
                                : 'border-gray-200 text-gray-600 hover:border-gray-300'
                            }`}
                          >
                            <i
                              className={`${
                                checked
                                  ? 'ri-checkbox-circle-fill text-teal-500'
                                  : 'ri-checkbox-blank-circle-line text-gray-300'
                              } text-base`}
                            ></i>
                            <span>{perm}</span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                </div>
              )}

              {/* 其他信息 */}
              {editTab === 'other' && (
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    {[
                      { label: '身份证号', key: 'idCard' },
                      { label: '居住地址', key: 'address' },
                      { label: '紧急联系人', key: 'emergencyContact' },
                      { label: '紧急联系电话', key: 'emergencyPhone' },
                    ].map((field) => (
                      <div key={field.key}>
                        <label className="block text-sm font-medium text-gray-700 mb-1.5">
                          {field.label}
                        </label>
                        <input
                          type="text"
                          value={
                            editingUser[
                              field.key as keyof UserInfo
                            ] as string
                          }
                          onChange={(e) =>
                            setEditingUser({
                              ...editingUser,
                              [field.key]: e.target.value,
                            })
                          }
                          className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                        />
                      </div>
                    ))}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">
                      备注说明
                    </label>
                    <textarea
                      rows={4}
                      value={editingUser.remark}
                      onChange={(e) => {
                        if (e.target.value.length <= 500) {
                          setEditingUser({
                            ...editingUser,
                            remark: e.target.value,
                          });
                        }
                      }}
                      maxLength={500}
                      placeholder="用户补充说明..."
                      className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm resize-none focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                    />
                    <p className="text-xs text-gray-400 mt-1 text-right">
                      {editingUser.remark.length}/500
                    </p>
                  </div>
                  <div className="p-4 bg-gray-50 rounded-xl">
                    <p className="text-xs font-medium text-gray-600 mb-2">
                      系统信息（只读）
                    </p>
                    <div className="grid grid-cols-2 gap-3 text-xs text-gray-500">
                      <div>
                        <span className="text-gray-400">入职日期：</span>
                        {editingUser.joinDate}
                      </div>
                      <div>
                        <span className="text-gray-400">最近登录：</span>
                        {editingUser.lastLogin}
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* 底部 */}
            <div className="border-t border-gray-200 px-6 py-4 flex items-center justify-between flex-shrink-0">
              <p className="text-xs text-gray-400">
                <i className="ri-time-line mr-1"></i>修改后立即生效
              </p>
              <div className="flex items-center space-x-3">
                <button
                  onClick={() => {
                    setShowEditModal(false);
                    setShowAddModal(false);
                    setEditingUser(null);
                  }}
                  className="px-6 py-2.5 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors text-sm font-medium whitespace-nowrap cursor-pointer"
                >
                  取消
                </button>
                <button
                  onClick={showAddModal ? handleSaveNew : handleSave}
                  className="px-8 py-2.5 bg-teal-600 text-white rounded-lg hover:bg-teal-700 hover:shadow-md transition-all text-sm font-medium whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-save-line mr-2"></i>
                  {showAddModal ? '创建用户' : '保存修改'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 删除确认 */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 backdrop-blur-sm">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6">
            <div className="w-12 h-12 bg-rose-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <i className="ri-alert-line text-2xl text-rose-600"></i>
            </div>
            <h3 className="text-lg font-semibold text-gray-800 text-center mb-2">
              确认删除用户？
            </h3>
            <p className="text-sm text-gray-500 text-center mb-6">
              此操作不可恢复，该用户的所有权限将被移除
            </p>
            <div className="flex items-center space-x-3">
              <button
                onClick={() => {
                  setShowDeleteConfirm(false);
                  setDeleteId(null);
                }}
                className="flex-1 px-4 py-2.5 border border-gray-300 text-gray-7
                  hover:bg-gray-50 transition-colors text-sm font-medium whitespace-nowrap cursor-pointer"
              >
                取消
              </button>
              <button
                onClick={handleDelete}
                className="flex-1 px-4 py-2.5 bg-rose-600 text-white rounded-lg hover:bg-rose-700 transition-colors text-sm font-medium whitespace-nowrap cursor-pointer"
              >
                确认删除
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
